import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import {
  deleteAssistantMemory,
  fetchAssistantMemory,
  resetAssistantMemory,
  type AssistantMemoryNote,
} from './assistantMemoryClient'
import type { AssistantEffort } from './useAssistantChat'
import { AssistantModelPicker } from './AssistantModelPicker'
import {
  ALL_CHART_TYPES,
  CHART_TYPE_LABELS,
  RANGE_PRESET_LABELS,
  SCAN_RECORD_CHOICES,
  toggleChartType,
  type AssistantChartType,
  type AssistantRangePreset,
  type AssistantToolPrefs,
} from './assistantToolPrefs'
import { TOUCH_MIN } from 'core/theme.tokens'

interface AssistantSettingsPopoverProps {
  effort: AssistantEffort
  onEffortChange: (effort: AssistantEffort) => void
  model: string
  onModelChange: (model: string) => void
  closeAfterNavigate: boolean
  onCloseAfterNavigateChange: (value: boolean) => void
  learnFromChats: boolean
  onLearnFromChatsChange: (value: boolean) => void
  toolPrefs: AssistantToolPrefs
  onToolPrefsChange: (prefs: AssistantToolPrefs) => void
  debugMode: boolean
  onDebugModeChange: (enabled: boolean) => void
  onClear: () => void
  disabled?: boolean
}

/** Gear menu inside the assistant drawer: effort, tools, clear history, nav behavior. */
export const AssistantSettingsPopover: React.FC<AssistantSettingsPopoverProps> = ({
  effort,
  onEffortChange,
  model,
  onModelChange,
  closeAfterNavigate,
  onCloseAfterNavigateChange,
  learnFromChats,
  onLearnFromChatsChange,
  toolPrefs,
  onToolPrefsChange,
  debugMode,
  onDebugModeChange,
  onClear,
  disabled = false,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const open = Boolean(anchor)
  const [memoryOpen, setMemoryOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [notes, setNotes] = useState<AssistantMemoryNote[]>([])
  const [notesLoading, setNotesLoading] = useState(false)
  const [notesError, setNotesError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [resetting, setResetting] = useState(false)

  useEffect(() => {
    if (!memoryOpen) return
    let cancelled = false
    setNotesLoading(true)
    setNotesError(null)
    void fetchAssistantMemory()
      .then(next => {
        if (!cancelled) setNotes(next)
      })
      .catch(() => {
        if (!cancelled) setNotesError('Could not load remembered notes')
      })
      .finally(() => {
        if (!cancelled) setNotesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [memoryOpen])

  const handleDeleteNote = (id: string) => {
    setDeletingId(id)
    void deleteAssistantMemory(id)
      .then(() => {
        setNotes(prev => prev.filter(note => note.id !== id))
      })
      .catch(() => {
        setNotesError('Could not delete that note')
      })
      .finally(() => {
        setDeletingId(null)
      })
  }

  const handleResetMemory = () => {
    setResetting(true)
    setNotesError(null)
    void resetAssistantMemory()
      .then(() => {
        setNotes([])
        setResetOpen(false)
      })
      .catch(() => {
        setNotesError('Could not reset memory')
      })
      .finally(() => {
        setResetting(false)
      })
  }

  const setChartTypeEnabled = (type: AssistantChartType, enabled: boolean) => {
    onToolPrefsChange(toggleChartType(toolPrefs, type, enabled))
  }

  return (
    <>
      <IconButton
        size='small'
        aria-label='Assistant settings'
        data-assistant-demo='settings'
        disabled={disabled}
        onClick={event => setAnchor(event.currentTarget)}
        sx={{ width: { xs: 40, sm: TOUCH_MIN }, height: { xs: 40, sm: TOUCH_MIN } }}
      >
        <SettingsOutlined fontSize='small' />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { p: { xs: 1.5, sm: 2 }, width: 300, maxWidth: 'calc(100vw - 24px)' },
          },
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ mb: 1.5, fontWeight: 700, fontSize: { xs: '0.8125rem', sm: undefined } }}
        >
          Assistant settings
        </Typography>

        <AssistantModelPicker value={model} onChange={onModelChange} active={open} />

        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
          Effort (tool-call budget)
        </Typography>
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <Select
            value={effort}
            onChange={event => {
              onEffortChange(event.target.value as AssistantEffort)
            }}
            inputProps={{ 'aria-label': 'Assistant effort' }}
          >
            <MenuItem value='low'>Low — faster, fewer tools</MenuItem>
            <MenuItem value='medium'>Medium</MenuItem>
            <MenuItem value='high'>High — deeper search</MenuItem>
          </Select>
        </FormControl>

        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
          Tools
        </Typography>
        {ALL_CHART_TYPES.map(type => (
          <FormControlLabel
            key={type}
            control={
              <Switch
                size='small'
                checked={toolPrefs.chartTypes.includes(type)}
                onChange={(_, checked) => setChartTypeEnabled(type, checked)}
              />
            }
            label={<Typography variant='body2'>{CHART_TYPE_LABELS[type]}</Typography>}
            sx={{ ml: 0, display: 'flex' }}
          />
        ))}
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5, mb: 1.5 }}>
          Turn on the charts the assistant may use. It picks one, then you can switch to the others.
        </Typography>

        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
          Max records
        </Typography>
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <Select
            value={toolPrefs.maxRecords}
            onChange={event => {
              onToolPrefsChange({ ...toolPrefs, maxRecords: Number(event.target.value) })
            }}
            inputProps={{ 'aria-label': 'Max records' }}
          >
            {SCAN_RECORD_CHOICES.map(choice => (
              <MenuItem key={choice} value={choice}>
                {choice.toLocaleString('en-US')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
          Date range
        </Typography>
        <FormControl fullWidth size='small' sx={{ mb: toolPrefs.rangePreset === 'custom' ? 1 : 2 }}>
          <Select
            value={toolPrefs.rangePreset}
            onChange={event => {
              onToolPrefsChange({
                ...toolPrefs,
                rangePreset: event.target.value as AssistantRangePreset,
              })
            }}
            inputProps={{ 'aria-label': 'Date range' }}
          >
            {(Object.keys(RANGE_PRESET_LABELS) as AssistantRangePreset[]).map(preset => (
              <MenuItem key={preset} value={preset}>
                {RANGE_PRESET_LABELS[preset]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {toolPrefs.rangePreset === 'custom' ? (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size='small'
              type='date'
              label='Start'
              value={toolPrefs.rangeStart}
              onChange={event => onToolPrefsChange({ ...toolPrefs, rangeStart: event.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size='small'
              type='date'
              label='End'
              value={toolPrefs.rangeEnd}
              onChange={event => onToolPrefsChange({ ...toolPrefs, rangeEnd: event.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        ) : null}

        <FormControlLabel
          control={
            <Switch
              checked={debugMode}
              onChange={(_, checked) => onDebugModeChange(checked)}
            />
          }
          label={<Typography variant='body2'>Debug mode</Typography>}
          sx={{ ml: 0, mb: 1.5 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={closeAfterNavigate}
              onChange={(_, checked) => {
                onCloseAfterNavigateChange(checked)
              }}
            />
          }
          label={
            <Typography variant='body2'>Close assistant after opening a link</Typography>
          }
          sx={{ mb: 1.5, ml: 0, alignItems: 'flex-start' }}
        />

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
            Remembered
          </Typography>
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={learnFromChats}
                onChange={(_, checked) => onLearnFromChatsChange(checked)}
                inputProps={{ 'aria-label': 'Learn from chats' }}
              />
            }
            label={<Typography variant='body2'>Learn from chats</Typography>}
            sx={{ ml: 0, display: 'flex' }}
          />
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
            This lets the assistant learn from your previous chats.
          </Typography>
          <Button
            fullWidth
            variant='outlined'
            sx={{ minHeight: TOUCH_MIN, textTransform: 'none' }}
            onClick={() => setMemoryOpen(true)}
          >
            View memory
          </Button>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
            History
          </Typography>
          <Button
            fullWidth
            variant='outlined'
            color='inherit'
            sx={{ minHeight: TOUCH_MIN }}
            onClick={() => {
              onClear()
              setAnchor(null)
            }}
          >
            Clear chat history
          </Button>
        </Box>
      </Popover>
      <Dialog open={memoryOpen} onClose={resetting ? undefined : () => setMemoryOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Memory</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Notes learned from your chats and anything you asked the assistant to remember.
          </Typography>
          {notesLoading ? <CircularProgress size={18} /> : null}
          {notesError ? (
            <Typography variant='body2' color='error' sx={{ mb: 1 }}>
              {notesError}
            </Typography>
          ) : null}
          {!notesLoading && notes.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Nothing remembered yet.
            </Typography>
          ) : null}
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {notes.map(note => {
              const when = note.createdAt ? new Date(note.createdAt) : null
              const stamp =
                when && !Number.isNaN(when.getTime())
                  ? when.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                  : ''
              return (
                <Box
                  key={note.id}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2'>{note.text}</Typography>
                    {stamp ? (
                      <Typography variant='caption' color='text.secondary'>
                        {stamp}
                      </Typography>
                    ) : null}
                  </Box>
                  <IconButton
                    size='small'
                    aria-label={`Delete ${note.text}`}
                    disabled={deletingId === note.id || resetting}
                    onClick={() => handleDeleteNote(note.id)}
                  >
                    <DeleteOutline fontSize='small' />
                  </IconButton>
                </Box>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color='error' disabled={notesLoading || resetting || notes.length === 0} onClick={() => setResetOpen(true)}>
            Reset memory
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setMemoryOpen(false)} disabled={resetting}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={resetOpen} onClose={resetting ? undefined : () => setResetOpen(false)}>
        <DialogTitle>Reset memory?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This deletes every remembered note for your account. Reset memory is a destructive action and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)} disabled={resetting}>
            Cancel
          </Button>
          <Button color='error' variant='contained' disabled={resetting} onClick={handleResetMemory}>
            {resetting ? 'Resetting…' : 'Reset memory'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
